import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Mobile uses the full /cart page instead of the slide-out drawer: the drawer is
 * a modal embedded in the header, and re-rendering it in place after a cart
 * change is unreliable on mobile (line items can vanish). At <=749px the cart
 * trigger is a plain link to /cart and add-to-cart does not auto-open the drawer.
 * Desktop keeps the drawer.
 */
const isMobileCart = () => window.matchMedia('(max-width: 749px)').matches;

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#trigger?.addEventListener('click', this.#handleTriggerClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#trigger?.removeEventListener('click', this.#handleTriggerClick);
  }

  get #trigger() {
    return this.querySelector('[data-cart-trigger]');
  }

  // Desktop: open the drawer instead of following the link. Mobile: do nothing,
  // so the link navigates to the full /cart page.
  #handleTriggerClick = (event) => {
    if (isMobileCart()) return;
    event.preventDefault();
    this.open();
  };

  #handleCartAdd = () => {
    if (isMobileCart()) return; // no drawer on mobile; the cart count still updates
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    // Safety net: if open() is invoked on mobile, send the customer to /cart.
    if (isMobileCart()) {
      window.location.href = this.#trigger?.getAttribute('href') || Theme.routes?.cart_url || '/cart';
      return;
    }

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
